import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, History, Zap, Plus,
  LogOut, User, ChevronDown, Users, Settings, Building2, Activity, TrendingUp, Layers
} from "lucide-react";
import AlertBell from "@/components/AlertBell";
import SynqLogo from "@/components/SynqLogo";

const USER_LINKS = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/admin/setup", label: "Organization", icon: Building2 },
  { path: "/pulse", label: "Pulse", icon: Activity },
  { path: "/trends", label: "Trends", icon: TrendingUp },
  { path: "/history", label: "History", icon: History },
];

const ADMIN_LINKS = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/campaigns", label: "Intelligence", icon: Layers },
  { path: "/admin/employees", label: "Employees", icon: Users },
  { path: "/admin/setup", label: "Organization", icon: Building2 },
  { path: "/trends", label: "Trends", icon: TrendingUp },
  { path: "/history", label: "History", icon: History },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);

  const isAdmin = user?.role === "admin";
  const hasOrg = !!user?.org_id;
  const isOrgUser = user?.account_type === "organization" || hasOrg;
  const NAV_LINKS = (isAdmin || isOrgUser) ? ADMIN_LINKS : USER_LINKS;

  const handleLogout = async () => {
    await logout();
    navigate("/auth");
  };

  return (
    <nav className="no-print sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
        {/* Logo */}
        <div
          data-testid="navbar-logo"
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2.5 cursor-pointer select-none"
        >
          <SynqLogo size={32} />
          <div className="flex items-baseline gap-1.5">
            <span className="font-outfit text-lg font-black text-slate-900 leading-none">SYNQ</span>
            <span className="font-ibm-plex text-xs text-slate-400 hidden sm:block">Project IQ</span>
          </div>
          {isAdmin && user?.org_name && (
            <span className="hidden md:flex items-center gap-1 ml-2 pl-2 border-l border-gray-200">
              <Building2 className="w-3 h-3 text-slate-400" />
              <span className="font-ibm-plex text-xs text-slate-400 max-w-[120px] truncate">{user.org_name}</span>
            </span>
          )}
        </div>

        {/* Nav Links */}
        <div className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map(({ path, label, icon: Icon }) => (
            <button
              key={path}
              data-testid={`nav-${label.toLowerCase()}`}
              onClick={() => navigate(path)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-outfit font-medium transition-colors ${
                location.pathname === path
                  ? "bg-slate-100 text-slate-900"
                  : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
              }`}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
              {label}
            </button>
          ))}
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-1">
          <AlertBell />
          <button
            data-testid="new-analysis-nav-btn"
            onClick={() => navigate("/project/new")}
            className="hidden sm:flex items-center gap-1.5 bg-slate-900 text-white font-outfit font-medium px-3 py-1.5 rounded-lg text-sm hover:bg-slate-800 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            New
          </button>

          {/* User Menu */}
          <div className="relative">
            <button
              data-testid="user-menu-btn"
              onClick={() => setShowMenu(!showMenu)}
              className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center ${isAdmin ? "bg-purple-100" : "bg-slate-200"}`}>
                {isAdmin ? <Building2 className="w-3.5 h-3.5 text-purple-600" /> : <User className="w-3.5 h-3.5 text-slate-600" />}
              </div>
              <span className="hidden sm:block font-ibm-plex text-sm text-slate-700 max-w-[120px] truncate">
                {user?.name || user?.email}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
            </button>

            {showMenu && (
              <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-xl shadow-lg py-1 z-50">
                <div className="px-4 py-2.5 border-b border-gray-100">
                  <p className="font-outfit text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
                  <p className="font-ibm-plex text-xs text-slate-400 truncate">{user?.email}</p>
                  {isAdmin && (
                    <span className="inline-block mt-1 font-ibm-plex text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded">Admin</span>
                  )}
                </div>
                <div className="py-1 md:hidden">
                  {NAV_LINKS.map(({ path, label, icon: Icon }) => (
                    <button key={path} onClick={() => { navigate(path); setShowMenu(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm font-outfit text-slate-700 hover:bg-gray-50 transition-colors">
                      <Icon className="w-4 h-4" strokeWidth={1.5} />
                      {label}
                    </button>
                  ))}
                  <div className="border-t border-gray-100 mt-1 pt-1" />
                </div>
                {isAdmin && !user?.org_id && (
                  <button onClick={() => { navigate("/admin/setup"); setShowMenu(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm font-outfit text-blue-600 hover:bg-blue-50 transition-colors">
                    <Settings className="w-4 h-4" />
                    Setup Organization
                  </button>
                )}
                {user?.is_super_admin && (
                  <button onClick={() => { navigate("/admin/super"); setShowMenu(false); }}
                    data-testid="super-admin-link"
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm font-outfit text-red-600 hover:bg-red-50 transition-colors">
                    <Settings className="w-4 h-4" />
                    Super Admin Panel
                  </button>
                )}
                <button onClick={() => { navigate("/pricing"); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm font-outfit text-slate-600 hover:bg-gray-50 transition-colors">
                  <Settings className="w-4 h-4" />
                  Plans & Pricing
                </button>
                <button
                  data-testid="logout-btn"
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-outfit text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
